import { DEFAULT_PAGE_SIZE, PICKER_PAGE_SIZE } from "@/lib/paging";
import { apiClient } from "./client";
import type { Role } from "@/store/auth-store";
import type { UserPublic } from "./auth-api";

export type Permission =
  | "can_manage_users"
  | "can_manage_organisation"
  | "can_take_test"
  | "can_manage_tests"
  | "can_upload_test_cases";

export interface RoleSpec {
  role: Role;
  permissions: Permission[];
  /** Permissions an ORG SETTING adds on top of the static role matrix —
   *  today only ``allow_sonographer_sign`` granting SONOGRAPHER
   *  draft+sign. Separate from ``permissions`` so the matrix can show
   *  that a toggle is holding the permission, and that switching it off
   *  takes it away. */
  granted_by_setting?: Permission[];
}

export interface AuditEntry {
  id: string;
  actor_id: string | null;
  action: string;
  target_id: string | null;
  ip_address: string | null;
  metadata_json: Record<string, unknown>;
  created_at: string;
}

export interface AdminUserCreate {
  username: string;
  full_name?: string | null;
  email?: string | null;
  role: Role;
  license_number?: string | null;
  /** Optional physical location. When set, SONOGRAPHER / RECEPTION only
   *  see studies at this location; leave blank for a single site. */
  location?: string | null;
  /** ISO timestamp after which the login stops working. Omit for an
   *  account that never expires — which is every clinical role. */
  access_expires_at?: string | null;
  password: string;
}

export interface AdminUserUpdate {
  full_name?: string | null;
  email?: string | null;
  role?: Role;
  active?: boolean;
  license_number?: string | null;
  location?: string | null;
}

export interface ResetPasswordResponse {
  user_id: string;
  temporary_password: string;
}

export interface UserPage {
  items: UserPublic[];
  page: number;
  page_size: number;
  /** The filtered set this page is drawn from. */
  total: number;
  /** Tab counts — both populations, always. */
  staff_total: number;
  candidate_total: number;
  /** Stat cards for the population in view, ignoring search and role. */
  active_total: number;
  sonographer_total: number;
  radiologist_total: number;
  expiring_total: number;
}

export interface UserQuery {
  includeInactive?: boolean;
  q?: string;
  role?: string;
  population?: "all" | "staff" | "candidates";
  page?: number;
  pageSize?: number;
}

export async function listUsersPage(query: UserQuery = {}): Promise<UserPage> {
  const { data } = await apiClient.get<UserPage>("/admin/users", {
    params: {
      include_inactive: query.includeInactive ?? false,
      ...(query.q ? { q: query.q } : {}),
      ...(query.role ? { role: query.role } : {}),
      population: query.population ?? "all",
      page: query.page ?? 1,
      page_size: query.pageSize ?? DEFAULT_PAGE_SIZE,
    },
  });
  return data;
}

/**
 * Every user, for a picker.
 *
 * Three callers need the whole list rather than a page — the audit
 * actor filter, the create-user form, and the assessment candidate
 * dropdown. Kept as its own function so a table and a dropdown cannot
 * quietly share one query and leave the dropdown missing options.
 */
export async function listUsers(includeInactive = false): Promise<UserPublic[]> {
  const page = await listUsersPage({
    includeInactive,
    pageSize: PICKER_PAGE_SIZE,
  });
  return page.items;
}

export async function createUser(body: AdminUserCreate): Promise<UserPublic> {
  const { data } = await apiClient.post<UserPublic>("/admin/users", body);
  return data;
}

export async function updateUser(
  userId: string,
  body: AdminUserUpdate,
): Promise<UserPublic> {
  const { data } = await apiClient.put<UserPublic>(`/admin/users/${userId}`, body);
  return data;
}

export async function deactivateUser(userId: string): Promise<void> {
  await apiClient.delete(`/admin/users/${userId}`);
}

export async function resetUserPassword(
  userId: string,
): Promise<ResetPasswordResponse> {
  const { data } = await apiClient.post<ResetPasswordResponse>(
    `/admin/users/${userId}/reset-password`,
  );
  return data;
}

export async function listRoles(): Promise<RoleSpec[]> {
  const { data } = await apiClient.get<RoleSpec[]>("/admin/roles");
  return data;
}

export interface AuditQuery {
  actor_id?: string;
  action?: string;
  /** ISO datetime, inclusive lower bound. */
  since?: string;
  /** ISO datetime, inclusive upper bound. The panel widens a picked date
   *  to 23:59:59 of that day, so "1st to 1st" means the whole 1st. */
  until?: string;
  /** Study accession. Matches rows whose target is this study, its
   *  worksheet or its reports, plus rows carrying the accession in
   *  metadata — ``target_id`` is polymorphic, so neither alone is enough. */
  accession?: string;
  page?: number;
  page_size?: number;
}

export interface AuditPage {
  items: AuditEntry[];
  page: number;
  page_size: number;
  total: number;
}

export async function listAudit(query: AuditQuery = {}): Promise<AuditPage> {
  const { data } = await apiClient.get<AuditPage>("/admin/audit", {
    params: query,
  });
  return data;
}

/** Distinct action values actually present in the log. Read from the DB
 *  rather than hardcoded, so a newly-added action type appears in the
 *  filter the first time it is written. */
export async function listAuditActions(): Promise<string[]> {
  const { data } = await apiClient.get<string[]>("/admin/audit/actions");
  return data;
}

// SR measurement gaps — the safety net over the web-side code→field mapping.
// A "gap" is a measurement that reached a worksheet with no home field, either
// because no rule mapped its code (``unmapped``) or the mapped field isn't on
// the study's template (``off_template``). See docs/sr-mapping-web-relocation.
export type MeasurementGapReason = "unmapped" | "off_template";

export interface MeasurementGap {
  id: string;
  template_id: string;
  reason: MeasurementGapReason;
  source_scheme: string;
  source_code: string;
  mapped_field: string;
  measurement_name: string | null;
  concept_meaning: string | null;
  label: string | null;
  parent_anatomy: string | null;
  laterality: string | null;
  sample_value: number | null;
  sample_unit: string | null;
  sample_accession: string | null;
  seen_count: number;
  first_seen: string;
  last_seen: string;
}

export interface MeasurementGapQuery {
  reason?: MeasurementGapReason;
  template_id?: string;
  limit?: number;
}

export async function listMeasurementGaps(
  query: MeasurementGapQuery = {},
): Promise<MeasurementGap[]> {
  const { data } = await apiClient.get<MeasurementGap[]>(
    "/admin/measurement-gaps",
    { params: query },
  );
  return data;
}

// Re-check: re-validate every recorded gap against the CURRENT mapping +
// routing. Rows whose measurement now finds an on-template home would no longer
// be produced, so they are cleared. Only the diagnostic index is touched — no
// worksheet is modified. Preview with dry_run first, then apply.
// Per-gap outcome. ``note`` is the whole point of the re-check: it says
// WHY a row survived — still unmapped, or maps fine but the sample study
// never had the value backfilled. Dropping it here (as this type used to)
// left the operator with a bare count and no way to tell a broken mapping
// from a stranded value.
export interface GapRecheckItem {
  id: string;
  template_id: string;
  reason: string;
  source_scheme: string;
  source_code: string;
  measurement_name: string | null;
  seen_count: number;
  resolved: boolean;
  resolved_field: string | null;
  target_template_id: string | null;
  note: string;
}

export interface GapRecheckResult {
  dry_run: boolean;
  total: number;
  cleared: number;
  kept: number;
  items: GapRecheckItem[];
}

export async function recheckMeasurementGaps(
  dryRun: boolean,
): Promise<GapRecheckResult> {
  const { data } = await apiClient.post<GapRecheckResult>(
    "/admin/measurement-gaps/recheck",
    null,
    { params: { dry_run: dryRun } },
  );
  return data;
}

// Per-study re-template + backfill: fix a study that landed on the wrong
// template (e.g. a urinary study stuck on `other`) by switching it to the
// template it would be routed to now and pulling its bucketed measurements
// into the new template's empty fields. Preview (GET) mutates nothing; apply
// (POST) is refused on a locked worksheet.
export interface RetemplatePreview {
  found: boolean;
  accession: string;
  worksheet_id: string | null;
  locked: boolean;
  current_template: string | null;
  target_template: string | null;
  would_change_template: boolean;
  would_fill: string[];
  still_off_template: string[];
  still_unmapped: number;
  note: string;
}

export interface RetemplateResult {
  ok: boolean;
  accession: string;
  worksheet_id: string | null;
  from_template: string | null;
  to_template: string | null;
  filled: string[];
  note: string;
}

/** ``targetTemplate`` overrides the auto-reclassified destination.
 *
 *  Without it the server derives the target from the STUDY DESCRIPTION,
 *  which is evidence about the order rather than about what the study
 *  turned out to be — a correctly re-templated early pregnancy ordered as
 *  a pelvic scan derives back to something else entirely. Naming the
 *  target is also how a run becomes a pure BACKFILL: pass the template it
 *  already has, and it fills the empty fields from the bucket without
 *  moving anything. */
export async function previewRetemplate(
  accession: string,
  targetTemplate?: string,
): Promise<RetemplatePreview> {
  const { data } = await apiClient.get<RetemplatePreview>(
    `/admin/studies/${encodeURIComponent(accession)}/retemplate`,
    targetTemplate ? { params: { target_template: targetTemplate } } : {},
  );
  return data;
}

export async function applyRetemplate(
  accession: string,
  targetTemplate?: string,
): Promise<RetemplateResult> {
  const { data } = await apiClient.post<RetemplateResult>(
    `/admin/studies/${encodeURIComponent(accession)}/retemplate`,
    null,
    targetTemplate ? { params: { target_template: targetTemplate } } : {},
  );
  return data;
}

export interface MailStatus {
  available: boolean;
  reason: string | null;
  from_address: string | null;
}

/** Can this system send email right now, and if not why. */
export async function mailStatus(): Promise<MailStatus> {
  const { data } = await apiClient.get<MailStatus>("/admin/mail/status");
  return data;
}

export interface MailResult {
  sent: boolean;
  to: string | null;
  detail: string | null;
}

/**
 * Email a user their login.
 *
 * The password is passed back because the server cannot recover it — it
 * hashed the value on reset, so the only surviving plaintext is the one
 * on this screen. Same authenticated channel it arrived on.
 */
export async function emailCredentials(
  userId: string,
  body: { password: string; email?: string },
): Promise<MailResult> {
  const { data } = await apiClient.post<MailResult>(
    `/admin/users/${userId}/email-credentials`,
    body,
  );
  return data;
}
